import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { subscribeUser } from '../pushNotification';
import { auth, googleProvider, db } from '../lib/firebase';
import { ref, get, update } from 'firebase/database';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        localStorage.setItem('userId', user.uid);
        
        // Sync profile photo in background
        (async () => {
          try {
            const userRef = ref(db, `users/${user.uid}`);
            const snapshot = await get(userRef);
            
            const googleProviderData = user.providerData?.find(p => p.providerId === 'google.com') || user.providerData?.[0];
            const googlePhotoUrl = googleProviderData?.photoURL || user.photoURL;
            let updatedPhotoUrl = null;
            
            if (snapshot.exists()) {
              const userData = snapshot.val();
              const currentDbPhotoUrl = userData.profilePhotoUrl;
              
              if (!currentDbPhotoUrl) {
                if (googlePhotoUrl) {
                  updatedPhotoUrl = googlePhotoUrl;
                } else {
                  const randomSeeds = ['Felix', 'Aneka', 'Oliver', 'Abby', 'Jack', 'Mia', 'Jude', 'Chloe', 'Leo', 'Zoe', 'Max', 'Luna'];
                  const randomSeed = randomSeeds[Math.floor(Math.random() * randomSeeds.length)];
                  updatedPhotoUrl = `https://api.dicebear.com/9.x/lorelei/svg?seed=${randomSeed}`;
                }
              } else if (googlePhotoUrl && currentDbPhotoUrl.includes('googleusercontent.com') && currentDbPhotoUrl !== googlePhotoUrl) {
                updatedPhotoUrl = googlePhotoUrl;
              }
              
              if (updatedPhotoUrl) {
                await update(userRef, { profilePhotoUrl: updatedPhotoUrl });
              }
            } else {
               // Initial user record setup if missing
               if (googlePhotoUrl) {
                 updatedPhotoUrl = googlePhotoUrl;
               } else {
                 const randomSeeds = ['Felix', 'Aneka', 'Oliver', 'Abby', 'Jack', 'Mia', 'Jude', 'Chloe', 'Leo', 'Zoe', 'Max', 'Luna'];
                 const randomSeed = randomSeeds[Math.floor(Math.random() * randomSeeds.length)];
                 updatedPhotoUrl = `https://api.dicebear.com/9.x/lorelei/svg?seed=${randomSeed}`;
               }
               
               await update(userRef, {
                 username: user.displayName || user.email?.split('@')[0] || 'User',
                 email: user.email,
                 profilePhotoUrl: updatedPhotoUrl,
                 createdAt: new Date().toISOString()
               });
            }
            
            if (updatedPhotoUrl) {
              const updateRankingPhoto = async (path: string) => {
                const rankingRef = ref(db, `${path}/${user.uid}`);
                const rankingSnapshot = await get(rankingRef);
                if (rankingSnapshot.exists()) {
                  await update(rankingRef, { photoURL: updatedPhotoUrl });
                }
              };

              await updateRankingPhoto('weekly');
              await updateRankingPhoto('monthly');
              await updateRankingPhoto('allTime');
            }
          } catch (error) {
            console.error("Error syncing profile on auth state change:", error);
          }
        })();
      } else {
        setCurrentUser(null);
        localStorage.removeItem('userId');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if(currentUser) {
      localStorage.setItem('userId', currentUser?.uid || '');
      subscribeUser(currentUser.uid);
    }
  }, [currentUser])

  async function signIn(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function signUp(email: string, password: string) {
    await createUserWithEmailAndPassword(auth, email, password);
  }

  async function signInWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    if (user) {
      try {
        const userRef = ref(db, `users/${user.uid}`);
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const userData = snapshot.val();
          
          const googleProviderData = user.providerData?.find(p => p.providerId === 'google.com') || user.providerData?.[0];
          const googlePhotoUrl = googleProviderData?.photoURL || user.photoURL;
          
          // Check if we need to update the Google profile photo
          // We update if they have no photo set, OR if they are currently using a Google photo and it changed
          const isGooglePhoto = userData.profilePhotoUrl?.includes('googleusercontent.com');
          if (googlePhotoUrl && (!userData.profilePhotoUrl || (isGooglePhoto && userData.profilePhotoUrl !== googlePhotoUrl))) {
            await update(userRef, { profilePhotoUrl: googlePhotoUrl });
            
            // Sync updated photo to rankings
            const updateRankingPhoto = async (path: string) => {
              const rankingRef = ref(db, `${path}/${user.uid}`);
              const rankingSnapshot = await get(rankingRef);
              if (rankingSnapshot.exists()) {
                await update(rankingRef, {
                  photoURL: googlePhotoUrl
                });
              }
            };

            await updateRankingPhoto('weekly');
            await updateRankingPhoto('monthly');
            await updateRankingPhoto('allTime');
          }
        } else {
          const googleProviderData = user.providerData?.find(p => p.providerId === 'google.com') || user.providerData?.[0];
          const googlePhotoUrl = googleProviderData?.photoURL || user.photoURL;
          
          // New user
          await update(userRef, {
            username: user.displayName || user.email?.split('@')[0],
            email: user.email,
            profilePhotoUrl: googlePhotoUrl,
            createdAt: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error("Error updating user profile with Google data:", error);
      }
    }
  }

  async function logout() {
    await signOut(auth);
  }

  const value = {
    currentUser,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div id="initial-loader">
          <div className="spinner"></div>
          <div className="loader-text">Loading LinguaLearn...</div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}